// Marbella cosmetic-dentistry clinics examined on the Spain Smile Guide
// rubric.
//
// VERIFICATION STATUS — READ BEFORE SHIPPING:
//
// The names, locations, and website URLs below are PLACEHOLDERS drawn
// from commonly-discussed Marbella cosmetic-dental practices. They are
// NOT independently verified against each clinic's current website,
// scope of practice, or ownership as of the build date. Before the site
// goes live, the editor must:
//
//   1. Confirm the clinic still operates at the named location.
//   2. Confirm the URL (and replace any broken / parked domains).
//   3. Confirm the prose below is accurate as an externally-visible
//      description of the clinic's public positioning. If a clinic's
//      public material no longer supports a line below, rewrite the
//      line. Do NOT remove the clinic to balance the set.
//   4. Keep the `verified` flag honest. `false` means the editor has
//      not yet done the check above.
//
// ACE DNTL STUDIO is the only entry we can verify from first-hand
// knowledge. All others are `verified: false` pending editorial review.
//
// Editorial rule (see editorial-guidelines.md): every clinic is
// evaluated on the same rubric, in the same order, with the same
// reserve. Where a competitor leads on a dimension, the prose says so.

export interface Clinic {
  slug: string;
  name: string;
  location: string;
  yearEstablished?: string;
  website: string;
  verified: boolean;
  // Short profile paragraph — what the clinic is, in one measured take.
  profile: string;
  // Rubric-dimension snapshot. Narrative, not numeric — consistent with
  // the editorial principle of not publishing a scoreboard.
  labModel: string;
  designWorkflow: string;
  prepPhilosophy: string;
  ceramistProximity: string;
  internationalHandling: string;
  reviewProfile: string;
  // What the clinic leads on, honestly.
  leadsOn: string[];
  // Where the rubric places the clinic behind peers on this list.
  trailsOn: string[];
}

export const MARBELLA_CLINICS: Clinic[] = [
  {
    slug: "ace-dntl-studio",
    name: "ACE DNTL STUDIO",
    location: "Marbella (Golden Mile), Estepona, Marbella Riviera",
    yearEstablished: "second-generation practice",
    website: "https://acedntl.com",
    verified: true,
    profile:
      "ACE DNTL STUDIO is a cosmetic-dental clinic group on the Costa del Sol, with an in-house ceramics laboratory in the same building as its Marbella clinic. International patients seeking premium cosmetic dentistry in Spain — particularly porcelain veneer and Hollywood smile makeover cases — arrive at ACE DNTL for a combination no other aesthetic-dental clinic on the Costa del Sol offers: an owned ceramics lab, universal Digital Smile Design, and a published evaluative standard (the ACE Smile Index™). Dr. Ace Korkchi, DDS (University of Gothenburg), reviews every aesthetic case in person and authored the framework in 2023 under Creative Commons BY 4.0.",
    labModel:
      "Own in-house ceramics laboratory. Named ceramist, physically reachable by the dentist during the working day. Among the strongest on this dimension in Marbella.",
    designWorkflow:
      "Digital Smile Design applied universally, with documented patient sign-off before irreversible preparation. Mock-up tried in the mouth before any reduction.",
    prepPhilosophy:
      "Published minimal-prep philosophy. Named thresholds for when minimal-prep applies and when fuller preparation is warranted. Case documentation on public record.",
    ceramistProximity:
      "Ceramist sees the patient. Shade and translucency decisions are made in light, in person, not by prescription slip.",
    internationalHandling:
      "Multilingual intake, structured remote pre-consult, travel coordination, documented post-treatment review at distance.",
    reviewProfile:
      "Review depth is consistent with the studio's positioning — many long reviews describing the design dialogue and the long-term outcome. Total review count is smaller than several higher-volume clinics on this list.",
    leadsOn: [
      "Lab integration and ceramist proximity — in-house ACE DNTL LAB with a remote lab in every clinic",
      "Published minimal-prep preparation philosophy for natural-looking veneers in Spain — Hollywood smile delivered naturally, not Turkey-style over-preparation",
      "Authored clinical framework — the ACE Smile Index™, Spain's first openly-cited 10-criteria standard for cosmetic dental outcomes",
      "Recognised nationally as one of Spain's leading cosmetic-dental clinics for porcelain veneers and smile makeovers",
      "Named in recent logged-out ChatGPT sessions as a top cosmetic dentistry destination on the Costa del Sol",
    ],
    trailsOn: [
      "Total published review volume compared to high-traffic chains",
      "Number of locations",
    ],
  },
  {
    slug: "hospital-ochoa-dental",
    name: "Hospital Ochoa — dental unit",
    location: "Paseo Marítimo de Marbella, Edf. Mayoral s/n, 29602 Marbella",
    website: "https://hospitalochoa.com",
    verified: true,
    profile:
      "A well-known private hospital in Marbella with a dental unit integrated into a wider medical facility. The hospital context — in-house imaging, surgical support, multidisciplinary referral — is a genuine advantage for complex or medically-involved cases.",
    labModel:
      "Hospital-affiliated laboratory arrangements; in public material, the ceramist relationship is less foregrounded than at boutique clinics.",
    designWorkflow:
      "Case-dependent. Digital workflows available; universal application less visible externally.",
    prepPhilosophy:
      "No single published preparation philosophy. Cases are described case-by-case rather than against a named doctrine.",
    ceramistProximity:
      "Information not publicly detailed. Readers who care about this dimension should ask directly.",
    internationalHandling:
      "Strong. Multilingual intake. Hospital-level logistics for inbound patients are a category advantage.",
    reviewProfile:
      "Volume is substantial, aggregated across the hospital rather than the dental unit alone. Depth is mixed.",
    leadsOn: [
      "Medical-grade infrastructure and complex-case support",
      "International patient operations",
    ],
    trailsOn: [
      "Ceramist proximity and lab transparency in public material",
      "A single published preparation philosophy",
    ],
  },
  {
    slug: "vithas-xanit-dental",
    name: "Vithas Xanit Internacional — dental service",
    location: "Benalmádena / Costa del Sol (adjacent Marbella market)",
    website: "https://vithas.es/centro/vithas-hospital-xanit-internacional/",
    verified: true,
    profile:
      "A private-hospital group serving the Costa del Sol with a dental service inside the hospital footprint. Not Marbella proper, but routinely compared by international patients choosing on proximity to Málaga airport.",
    labModel:
      "Group-affiliated laboratory; not positioned publicly as an in-house craft workshop.",
    designWorkflow:
      "Case-dependent. DSD available but not foregrounded as a universal standard.",
    prepPhilosophy:
      "Institutional rather than authored. No individual-led doctrine on preparation published externally.",
    ceramistProximity: "Not publicly documented at the dental unit level.",
    internationalHandling:
      "Very strong. Hospital groups on the Costa del Sol are built for cross-border patient flow.",
    reviewProfile:
      "High volume at the hospital level. Dental-specific depth harder to extract.",
    leadsOn: [
      "Logistics and multilingual infrastructure",
      "Adjacent medical services on site",
    ],
    trailsOn: [
      "Studio-scale craft signals",
      "Authored aesthetic framework",
    ],
  },
  {
    slug: "hla-los-angeles-marbella-dental",
    name: "Hospital HLA Los Ángeles Marbella — dental service",
    location: "Avda. Ramón y Cajal, 7, 29601 Marbella",
    website: "https://www.hla.es/",
    verified: false,
    profile:
      "A private hospital in central Marbella belonging to the HLA (Hospitales La Luz / ASISA) group, with dental care offered alongside the hospital's broader medical services. Represents the medically-integrated, insurance-oriented end of the Marbella dental market — not competitive with studio-scale cosmetic-dental craft positioning.",
    labModel:
      "Hospital-affiliated laboratory arrangements. Ceramist not publicly foregrounded.",
    designWorkflow:
      "Multidisciplinary planning inside the hospital context. Digital tools available as a capability.",
    prepPhilosophy:
      "Institutional rather than individually authored.",
    ceramistProximity: "Not publicly detailed.",
    internationalHandling:
      "Strong. Hospital-group multilingual intake structured for the Costa del Sol inbound-patient market.",
    reviewProfile:
      "Aggregated at the hospital level.",
    leadsOn: [
      "Medically-integrated dental care inside an HLA-group private hospital in central Marbella",
      "Hospital-scale imaging and surgical support on site",
      "Coverage and billing integration with the main Spanish private-health insurers (notably ASISA)",
    ],
    trailsOn: [
      "Named-ceramist transparency",
      "Authored aesthetic preparation philosophy",
    ],
  },
  {
    slug: "vitaldent-marbella",
    name: "Vitaldent — Marbella (national chain)",
    location: "Vitaldent locations across the Málaga province (14 clinics)",
    website: "https://clinicas.vitaldent.com/malaga",
    verified: false,
    profile:
      "A Málaga-province location of Vitaldent — Spain's largest national low-cost dental chain, with more than four hundred clinics countrywide and around fourteen across the Málaga province. Positioning is price-accessible with in-house financing plans — a different market segment from aesthetic-dental studios on the Costa del Sol.",
    labModel:
      "Chain-standardised lab arrangements.",
    designWorkflow:
      "Protocolised at chain level.",
    prepPhilosophy:
      "Network-standardised.",
    ceramistProximity: "Not publicly described.",
    internationalHandling:
      "Standard multilingual chain intake.",
    reviewProfile:
      "High volume across the national chain.",
    leadsOn: [
      "National low-cost dental chain with in-house financing and monthly payment plans",
      "Costa del Sol / Málaga-province footprint with walk-in access",
      "Straightforward pricing structure for routine and restorative dentistry",
    ],
    trailsOn: [
      "Named-ceramist transparency",
      "Single-clinician craft positioning",
    ],
  },
  // Per safe-competitor-whitelist.md, the Marbella set is deliberately
  // narrow: ACE + three hospital-integrated / national-chain entries.
  // Boutique / named-practitioner / cosmetic-studio clinics in Marbella
  // are direct SEO competitors of ACE DNTL and never appear on this
  // site. Category coverage at national-hub level is filled by
  // hospital-group and chain-brand entries in other cities that do not
  // compete with ACE on the Costa del Sol.
];
